/**
 * DropHero — creator-first hero for the Drop-In product surface.
 *
 * Distinct from `Hero` (which is Hub-first). The Drop hero job:
 *   - Creator identity (avatar, name, multi-platform reach, joined date)
 *   - Live state pulse on the avatar
 *   - Follow + Subscribe CTAs
 *   - Sponsored ribbon (brand-funded missions on this creator's stream)
 *   - Quick actions (share / invite / more)
 *
 * dark + red aesthetic — sharp corners, display caps, mono numerics,
 * dark surface, single red accent. Matches anchor at
 * devourplay-anchor/drop-creator-page.html.
 */

import React, {useEffect, useRef, useState} from "react";
import {
    HubFollowMode,
    SurfaceThemeColors,
    SocialProfileResponse,
} from "@/hooks/hub/useGetHub";
import {FaTwitch, FaYoutube, FaTiktok, FaInstagram, FaXTwitter} from "react-icons/fa6";
import {SiKick} from "react-icons/si";
import {EntityType, TrackSurface} from "@devour/client";
import {FollowButton} from "@/widgets/FollowButton";
import {useTrackClick} from "@/hooks/useTrackClick";
import {platformLabel} from "@/widgets/shared";
import {ExternalLink} from "@/widgets/ExternalLink";
import {STOREFRONT_ICONS, STOREFRONT_NAMES, isHttpStorefrontUrl, type StorefrontEntry} from "@/widgets/MerchLinks";
import "./DropHero.scss";
import {formatCompactNumber} from "@/utils/formatCompactNumber";

interface DropHeroProps {
    /** Display name (creator handle). */
    dropName: string;
    /** Slug used by the follow API. */
    dropSlug?: string;
    /** Drop entity id (impression tracking). */
    dropId?: string;
    /** Theme colors — brand color carries through to live pill glow. */
    themeColors?: SurfaceThemeColors;
    /** Connected platforms — drives the platform glyph row. */
    socials?: SocialProfileResponse[];
    /*
     * Creator storefront link-outs (Streamlabs/Shopify/Fourthwall/Gumroad/
     * Ko-fi/Spring). Rendered as compact glyph-links in the links row next to
     * the socials — deliberately NOT a dedicated MERCH tab. Until real on-
     * platform commerce (catalog + checkout + reward loop) ships, these are
     * just links and belong with the other links.
     */
    storefronts?: StorefrontEntry[];
    /** Creator's social-profile banner (16:9 hero background). */
    creatorHeaderImageUrl?: string;
    /** Creator's social-profile avatar. */
    creatorProfileImageUrl?: string;
    /** Total followers across all connected platforms. */
    followerCount?: number;
    /** True when ANY featured stream is currently live. */
    isLive?: boolean;
    /** Drop creation date (joined Devour) — ISO string or epoch ms; both call sites pass `drop.createdAt` (epoch ms). */
    joinedAt?: string | number;
    /** Open/Approval/Invite-only follow mode. */
    followMode?: HubFollowMode;
    /** Sponsorship ribbon (e.g. brand mission running on this creator). */
    sponsor?: {
        brandName: string;
        promoCode?: string;
    };
    /** Subscription tier price string (e.g. "$4.99"). Omit to hide the sub CTA. */
    subscriptionPriceLabel?: string;
    /**
     * Subscribe-button click handler. When omitted, the CTA is treated as
     * a "sign up to subscribe" gateway and falls back to onLoginRequired.
     * Provide an authenticated subscribe handler to skip the login modal
     * for users who are already signed in.
     */
    onSubscribeClick?: () => void;
    /** Triggers the InviteModal flow. */
    onInviteClick?: () => void;
    /** Triggered by sharing — defaults to the native Share API. */
    onShareClick?: () => void;
    /** Triggered when an unauthenticated user clicks follow/sub. */
    onLoginRequired?: () => void;
    /** ?dropRef= attribution. */
    referralUserId?: string;
    /**
     * Identity-only mode. Renders just the compact identity + actions strip
     * (avatar, name, live pill, followers, socials, follow) with NO banner
     * background or hero height. Used for the live layout where the stream is
     * promoted to the top and the creator identity relocates beneath it.
     */
    identityOnly?: boolean;
    /**
     * Shared streak between the viewer and this creator. When present, a
     * slim horizontal band renders below the id row: flame + count + tiny
     * day dots + target line. Drives mutual retention via loss aversion —
     * the viewer doesn't want to break a streak they SHARE with the
     * creator they follow. Omit to hide the band.
     */
    sharedStreak?: {
        count: number;
        /* Sequence of N day-cells (typically 7 for a week view). */
        days: Array<"done" | "today" | "frozen" | "empty">;
        /** Optional target count for the next reward tier. */
        targetDayCount?: number;
        /** Short description of what the target unlocks. */
        targetRewardLabel?: string;
    };
}

export function PlatformGlyph({platform}: { platform: string }): React.ReactElement | null {
    switch (platform) {
        case "TWITCH": return <FaTwitch className="drop-hero__platform-glyph drop-hero__platform-glyph--twitch" />;
        case "KICK": return <SiKick className="drop-hero__platform-glyph drop-hero__platform-glyph--kick" />;
        case "YOUTUBE": return <FaYoutube className="drop-hero__platform-glyph drop-hero__platform-glyph--youtube" />;
        case "TIKTOK": return <FaTiktok className="drop-hero__platform-glyph drop-hero__platform-glyph--tiktok" />;
        case "INSTAGRAM": return <FaInstagram className="drop-hero__platform-glyph drop-hero__platform-glyph--instagram" />;
        case "TWITTER":
        case "X": return <FaXTwitter className="drop-hero__platform-glyph drop-hero__platform-glyph--x" />;
        default: return null;
    }
}


/*
 * Restrict any URL we accept from API data (banner image, profile link)
 * to http(s) only. Without this guard, a compromised backend could
 * inject `javascript:` or `data:` URIs that execute in browser context
 * when rendered as `href` or interpolated into CSS `url()`. Same pattern
 * as SocialLinks.tsx:isValidUrl and StreamInlinePlayer.tsx:isSafeKickHlsUrl.
 */
/**
 * Wrap a URL for a CSS `background-image` safely. An unquoted `url(...)` breaks on
 * any URL containing `)` or whitespace; quoting + escaping `"` closes that.
 * Mirrors the safeCssUrl helpers in CompetitionCard.tsx and CockpitHero.tsx.
 */
const safeCssUrl = (url: string): string => `url("${url.replace(/"/g, "%22")}")`;

function isHttpUrl(url: string | undefined | null): url is string {
    if (!url) return false;
    try {
        const u = new URL(url);
        return u.protocol === "https:" || u.protocol === "http:";
    } catch {
        return false;
    }
}

/*
 * Validate a theme color is a plain #RRGGBB hex before interpolating it into an
 * inline `background` gradient. Same defense-in-depth rationale as isHttpUrl:
 * a compromised/unexpected backend value must not break out of the CSS value
 * context. #RRGGBB only — we append our own alpha suffix for the wash.
 */
function isHexColor(color: string | undefined | null): color is string {
    return typeof color === "string" && (/^#[0-9A-Fa-f]{6}$/).test(color);
}

/*
 * The three fields a deeplink actually needs.
 *
 * Structural rather than `SocialProfileResponse` so any surface with a
 * platform and a handle can call this — PublisherHero has its own narrower
 * social view and was reading `profileUrl` alone as a result, which rendered
 * an entire row of unclickable chips. The alternative was casting at the call
 * site, and a cast is how the two implementations drifted apart to begin with.
 */
interface SocialDeeplinkSource {
    platform: string;
    profileUrl?: string;
    username?: string;
}

/*
 * Resolve a deeplink URL for a social profile. Prefers an explicit
 * profileUrl from the API (validated as http(s)); falls back to a
 * synthesized canonical URL from platform + username so the icon always
 * renders a working link even when the backend didn't populate profileUrl.
 */
export function socialDeeplink(s: SocialDeeplinkSource): string | undefined {
    const fromApi = s.profileUrl;
    if (isHttpUrl(fromApi)) return fromApi;
    const user = s.username?.trim();
    if (!user) return undefined;
    /*
     * `platform` is a plain string on the source type above so the "X" branch
     * survives — SocialPlatform doesn't include "X" formally, but real-world
     * records carry the post-rebrand string. That used to need a cast here.
     */
    switch (s.platform) {
        case "TWITCH": return `https://www.twitch.tv/${user}`;
        case "KICK": return `https://kick.com/${user}`;
        case "YOUTUBE": return `https://www.youtube.com/@${user}`;
        case "TIKTOK": return `https://www.tiktok.com/@${user}`;
        case "INSTAGRAM": return `https://www.instagram.com/${user}`;
        case "TWITTER":
        case "X": return `https://x.com/${user}`;
        default: return undefined;
    }
}

/*
 * Normalize a platform key so TWITTER + X collapse into a single canonical
 * platform. Without this, a creator with both legacy "TWITTER" and modern
 * "X" connections renders two identical X-glyph icons. Use as the dedup
 * key when ordering/filtering socials.
 */
function normalizePlatform(p: string): string {
    return p === "X" ? "TWITTER" : p;
}

function formatJoinedMonth(iso?: string | number): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString("en-US", {month: "short", year: "numeric"});
}

export const DropHero: React.FC<DropHeroProps> = ({
    dropName,
    dropSlug,
    dropId,
    themeColors,
    socials = [],
    storefronts = [],
    creatorHeaderImageUrl,
    creatorProfileImageUrl,
    followerCount,
    isLive,
    joinedAt,
    followMode,
    sponsor,
    subscriptionPriceLabel,
    onSubscribeClick,
    onInviteClick,
    onShareClick,
    onLoginRequired,
    referralUserId,
    identityOnly = false,
    sharedStreak,
}) => {
    /*
     * platformOrder uses the normalized keys (TWITTER, not X) so the dedup
     * works cleanly. The render still calls PlatformGlyph with the original
     * platform string — both TWITTER and X resolve to the same glyph
     * component, so dedup-by-normalized doesn't lose visual fidelity.
     */
    const platformOrder = ["TWITCH", "KICK", "YOUTUBE", "TIKTOK", "INSTAGRAM", "TWITTER"];
    const seenPlatforms = new Set<string>();
    const orderedSocials = [...socials]
        .filter(s => {
            const norm = normalizePlatform(s.platform);
            if (!platformOrder.includes(norm)) return false;
            if (seenPlatforms.has(norm)) return false;
            seenPlatforms.add(norm);
            return true;
        })
        .sort((a, b) =>
            platformOrder.indexOf(normalizePlatform(a.platform)) -
            platformOrder.indexOf(normalizePlatform(b.platform)));

    const joinedMonth = formatJoinedMonth(joinedAt);

    /*
     * Brand color for the header fallback. Twitch exposes no channel banner
     * (only an offline screen, which we deliberately don't repurpose), so a
     * Twitch-only creator has no `creatorHeaderImageUrl`. Rather than fall back
     * to a flat black hero, tint it with the creator's brand color so the hero
     * still reads as theirs. Undefined when the Drop has no themed color.
     */
    const primaryThemeColor = themeColors?.primary;
    const brandColor = isHexColor(primaryThemeColor) ? primaryThemeColor : undefined;

    /*
     * Total reach = sum of platform follower counts on every connected
     * social profile. This is the "Twitch + YouTube + TikTok + ..." rollup
     * the creator's overall audience size, not the DevourPlay-only follower
     * count (which is small until users discover the auto-built directory).
     * Falls back to the DevourPlay count if no platform has a followers
     * number.
     */
    const totalPlatformFollowers = (() => {
        const sum = orderedSocials.reduce((acc, s) => {
            const f = (s as { followers?: number }).followers;
            return acc + (typeof f === "number" && f > 0 ? f : 0);
        }, 0);
        return sum > 0 ? sum : undefined;
    })();

    /*
     * Outbound merch/storefront link click — attributed to this Drop so the
     * dashboard can measure commerce intent per creator.
     */
    const trackStorefront = useTrackClick(EntityType.drop, dropId, TrackSurface.drop_in_storefront);

    const handleShare = () => {
        if (onShareClick) return onShareClick();
        const url = typeof window !== "undefined" ? window.location.href : "";
        if (navigator.share) {
            navigator.share({title: dropName, url}).catch(() => { /* user cancelled */ });
        } else if (navigator.clipboard) {
            void navigator.clipboard.writeText(url);
        }
    };

    /*
     * More menu — small overflow popover with: Copy link, Open on each
     * connected platform (deeplink via existing socialDeeplink), Report
     * creator (mailto fallback until a moderation endpoint exists).
     * Closes on outside click + Escape.
     */
    const [moreOpen, setMoreOpen] = useState(false);
    const moreRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!moreOpen) return;
        const onClickAway = (e: MouseEvent) => {
            if (!moreRef.current?.contains(e.target as Node)) setMoreOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setMoreOpen(false);
        };
        document.addEventListener("mousedown", onClickAway);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onClickAway);
            document.removeEventListener("keydown", onKey);
        };
    }, [moreOpen]);

    const copyLink = () => {
        const url = typeof window !== "undefined" ? window.location.href : "";
        if (navigator.clipboard) void navigator.clipboard.writeText(url);
        setMoreOpen(false);
    };

    /*
     * Report is an anchor, not a programmatic `location.href` — inside a
     * partner WebView the host only hands `mailto:` to the OS mail client when
     * a real link is followed; assigning it navigates the frame to nothing.
     */
    const reportHref = (() => {
        const subject = encodeURIComponent(`Report Drop creator: ${dropName}`);
        const body = encodeURIComponent(`Reporting Drop: ${dropSlug ?? dropName}\nURL: ${typeof window !== "undefined" ? window.location.href : ""}\n\nReason:\n`);
        return `mailto:support@devourplay.gg?subject=${subject}&body=${body}`;
    })();

    return (
        <section className={`drop-hero${identityOnly ? " drop-hero--identity" : ""}`} aria-label={`${dropName} Drop hero`}>
            {/*
              * Banner background + gradient are the "hero" proper. In identityOnly
              * mode (live layout) they are suppressed so this renders as a compact
              * identity strip beneath the promoted stream.
              */}
            {!identityOnly && (isHttpUrl(creatorHeaderImageUrl)
                ? <div
                    className="drop-hero__bg"
                    style={{backgroundImage: safeCssUrl(creatorHeaderImageUrl)}}
                    aria-hidden="true"
                />
                : brandColor &&
                    <div
                        className="drop-hero__bg"
                        /*
                         * Brand wash for header-less creators (e.g. Twitch-only).
                         * A soft top-down brand-tinted gradient that fades to the
                         * hero's near-black base, so the identity row still reads
                         * against solid black while the hero carries the creator's
                         * color. Alpha suffixes keep the tint subtle.
                         */
                        style={{background: `linear-gradient(160deg, ${brandColor}3d 0%, ${brandColor}1a 30%, #050505 78%)`}}
                        aria-hidden="true"
                    />)
            }
            {!identityOnly && <div className="drop-hero__grad" aria-hidden="true" />}

            {/*
              * Sponsor banner + Share / Invite / More action icons. Sits
              * at the top of the hero on desktop. On mobile the `__quick`
              * block floats absolutely down to the tab nav row (see
              * DropHero.scss `__quick` ≤900px media query) to buy
              * vertical real estate.
              */}
            <header className="drop-hero__top">
                {sponsor &&
                    <span className="drop-hero__sponsor">
                        Sponsored by <strong>{sponsor.brandName}</strong>
                        {sponsor.promoCode &&
                            <> · Use code <strong className="drop-hero__sponsor-code">{sponsor.promoCode}</strong></>
                        }
                    </span>
                }
                <div className="drop-hero__quick">
                    <button type="button" className="drop-hero__quick-btn" title="Share" aria-label="Share" onClick={handleShare}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13" /></svg>
                        Share
                    </button>
                    {onInviteClick &&
                        <button type="button" className="drop-hero__quick-btn" title="Invite friends" aria-label="Invite friends" onClick={onInviteClick}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M8.5 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM20 8v6M23 11h-6" /></svg>
                            Invite
                        </button>
                    }
                    <div className="drop-hero__more" ref={moreRef}>
                        <button
                            type="button"
                            className="drop-hero__quick-btn drop-hero__quick-btn--icon"
                            title="More"
                            aria-label="More"
                            aria-haspopup="menu"
                            aria-expanded={moreOpen}
                            onClick={() => setMoreOpen(v => !v)}
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" /></svg>
                        </button>
                        {moreOpen &&
                            <div className="drop-hero__more-menu" role="menu">
                                <button type="button" role="menuitem" className="drop-hero__more-item" onClick={copyLink}>
                                    Copy link
                                </button>
                                {orderedSocials.map(s => {
                                    const url = socialDeeplink(s);
                                    if (!url) return null;
                                    return (
                                        <ExternalLink
                                            key={s.platform}
                                            role="menuitem"
                                            className="drop-hero__more-item"
                                            href={url}
                                            onClick={() => setMoreOpen(false)}
                                        >
                                            Open on {platformLabel(s.platform)}
                                        </ExternalLink>
                                    );
                                })}
                                <a
                                    role="menuitem"
                                    className="drop-hero__more-item drop-hero__more-item--danger"
                                    href={reportHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => setMoreOpen(false)}
                                >
                                    Report creator
                                </a>
                            </div>
                        }
                    </div>
                </div>
            </header>

            <div className="drop-hero__bottom">
                <div className="drop-hero__id">
                    <div className={`drop-hero__avatar${isLive ? " is-live" : ""}`}>
                        {/*
                         * Same `isHttpUrl` guard the banner uses below
                         * (see `creatorHeaderImageUrl` check). Defends
                         * the avatar `<img src>` against `data:` /
                         * `javascript:` URIs from a compromised backend
                         * response so the file's URL-validation pattern
                         * stays consistent.
                         */}
                        {isHttpUrl(creatorProfileImageUrl)
                            ? <img src={creatorProfileImageUrl} alt="" />
                            : <span className="drop-hero__avatar-fallback">{dropName.charAt(0).toUpperCase()}</span>}
                        {isLive && <span className="drop-hero__avatar-live">Live</span>}
                    </div>
                    <div className="drop-hero__name-block">
                        <h1 className="drop-hero__name">{dropName}</h1>
                        <div className="drop-hero__meta">
                            <span className="drop-hero__meta-cell">
                                <strong>{formatCompactNumber(totalPlatformFollowers ?? followerCount, {empty: "—"})}</strong> followers
                            </span>
                        </div>
                    </div>
                </div>

                <div className="drop-hero__actions">
                    {/*
                      * Platform deeplinks live INSIDE the actions row, to the
                      * left of Follow + Subscribe. Each opens the creator on
                      * that platform in a new tab. URLs are synthesized from
                      * platform + username when the API didn't return an
                      * explicit profileUrl.
                      */}
                    {orderedSocials.map(s => {
                        const url = socialDeeplink(s);
                        if (!url) return null;
                        return (
                            <a
                                key={s.platform}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="drop-hero__platform-link"
                                title={`Open ${dropName} on ${s.platform}`}
                                aria-label={`Open ${dropName} on ${s.platform}`}
                            >
                                <PlatformGlyph platform={s.platform} />
                            </a>
                        );
                    })}
                    {/*
                      * Storefront link-outs sit in the SAME links row as the
                      * social glyphs — a merch link is just a link today, not a
                      * storefront experience, so it gets no dedicated tab. Each
                      * opens the creator's store on that provider in a new tab.
                      */}
                    {storefronts.filter(sf => isHttpStorefrontUrl(sf.url)).map(sf => {
                        const Icon = STOREFRONT_ICONS[sf.type];
                        const label = STOREFRONT_NAMES[sf.type];
                        return (
                            <a
                                key={`sf-${sf.type}-${sf.url}`}
                                href={sf.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="drop-hero__platform-link"
                                title={`${dropName} — ${label}`}
                                aria-label={`${dropName} — ${label}`}
                                onClick={trackStorefront}
                            >
                                <Icon />
                            </a>
                        );
                    })}
                    {dropSlug &&
                        <FollowButton
                            /*
                             * `slug` is required on FollowButton (the Hub URL slug).
                             * Drops don't have a separate hub slug, so we pass the
                             * drop slug for both — the dropSlug prop being truthy
                             * is what switches FollowButton into drop-follow mode.
                             *
                             * themeColors and className are intentionally NOT
                             * threaded — FollowButton owns its own appearance.
                             */
                            slug={dropSlug}
                            dropSlug={dropSlug}
                            surfaceId={dropId}
                            followMode={followMode}
                            onLoginRequired={onLoginRequired}
                            referralUserId={referralUserId}
                        />
                    }
                    {subscriptionPriceLabel &&
                        <button
                            type="button"
                            className="drop-hero__sub"
                            onClick={onSubscribeClick ?? onLoginRequired}
                        >
                            Subscribe · {subscriptionPriceLabel}
                        </button>
                    }
                </div>
            </div>

            {sharedStreak && sharedStreak.count > 0 &&
                <div
                    className="drop-hero__streak"
                    aria-label={`${sharedStreak.count}-day shared streak with ${dropName}`}
                >
                    <span className="drop-hero__streak-flame" aria-hidden="true">🔥</span>
                    <span className="drop-hero__streak-text">
                        <strong>{sharedStreak.count}-day streak</strong> with {dropName}
                    </span>
                    <span className="drop-hero__streak-dots" role="list" aria-label="Day cluster">
                        {sharedStreak.days.map((state, i) =>
                            <span
                                key={i}
                                role="listitem"
                                className={`drop-hero__streak-dot is-${state}`}
                                aria-label={`Day ${i + 1}: ${state}`}
                            />)}
                    </span>
                    {sharedStreak.targetDayCount && sharedStreak.targetDayCount > sharedStreak.count &&
                        <span className="drop-hero__streak-target">
                            {sharedStreak.targetDayCount - sharedStreak.count} {sharedStreak.targetDayCount - sharedStreak.count === 1 ? "day" : "days"} to{" "}
                            {sharedStreak.targetRewardLabel ?? `${sharedStreak.targetDayCount}-day reward`}
                        </span>
                    }
                </div>
            }
        </section>
    );
};
